import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { staffProfileService } from '../services/staffProfileService';
import { nekoStaff } from 'public-contracts';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';

// Lucide React icons
import { Save, AlertTriangle, Briefcase } from 'lucide-react';

type StaffProfile = nekoStaff.StaffProfile;

interface StaffProfileEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  profile: StaffProfile | null;
  userId?: string; // Optional userId to create profile if profile is null
}

export const ModernStaffProfileEditDialog: React.FC<StaffProfileEditDialogProps> = ({
  open,
  onClose,
  onSuccess,
  profile,
  userId,
}) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    address: '',
    postionId: '',
    departmentId: '',
    ratePerHour: 0,
    ratePerHourCurrency: 'VND',
    sex: '',
    // Extended fields stored in metadata
    employmentType: 'part-time' as 'full-time' | 'part-time' | 'contract',
    vacationDaysPerYear: 0,
    vacationDaysUsed: 0,
    baseSalary: 0,
    guaranteedHoursPerWeek: 0,
  });

  // Reset form when dialog opens/closes or profile changes
  useEffect(() => {
    if (open) {
      // Always try to fetch the latest profile data, even if profile is provided
      // This ensures we have the most up-to-date data and handle duplicates correctly
      const loadProfileData = async () => {
        let profileToLoad = profile;
        
        // Determine userId to fetch with
        const targetUserId = profile?.userId || userId;
        
        // Always fetch fresh profile data to ensure we have the latest and most complete profile
        if (targetUserId) {
          try {
            console.log(`🔍 Fetching profile for userId: ${targetUserId}${profile ? ' (profile provided but fetching fresh)' : ''}`);
            const fetchedProfile = await staffProfileService.getStaffProfileByUserId(targetUserId);
            
            // Validate the fetched profile has required fields
            if (!fetchedProfile.id) {
              console.error('❌ Fetched profile missing id field!', fetchedProfile);
              throw new Error('Profile missing id field');
            }
            
            profileToLoad = fetchedProfile;
            console.log(`✅ Fetched profile:`, {
              profileId: profileToLoad.id,
              userId: profileToLoad.userId,
              hasMetadata: !!(profileToLoad as any).metadata,
              metadataKeys: (profileToLoad as any).metadata ? Object.keys((profileToLoad as any).metadata) : [],
            });
          } catch (err) {
            console.log(`   Profile not found for userId ${targetUserId}, will create new one`);
            // If fetch fails and we have a profile passed in, use it as fallback
            if (!profileToLoad) {
              profileToLoad = null;
            }
          }
        }
        
        if (profileToLoad && profileToLoad.id) {
          const metadata = (profileToLoad as any).metadata || {};
          
          // Debug logging to help diagnose loading issues
          console.log('📋 Loading profile data into form:', {
            profileId: profileToLoad.id,
            userId: profileToLoad.userId,
            hasMetadata: !!metadata,
            metadataKeys: Object.keys(metadata),
            employmentType: metadata.employmentType || (profileToLoad as any).employmentType,
            baseSalary: metadata.baseSalary || (profileToLoad as any).baseSalary,
            guaranteedHours: metadata.guaranteedHoursPerWeek || (profileToLoad as any).guaranteedHoursPerWeek,
            vacationDays: metadata.vacationDaysPerYear || (profileToLoad as any).vacationDaysPerYear,
          });
          
          setFormData({
            address: profileToLoad.address || '',
            postionId: profileToLoad.postionId || '',
            departmentId: profileToLoad.departmentId || '',
            ratePerHour: profileToLoad.ratePerHour || 0,
            ratePerHourCurrency: profileToLoad.ratePerHourCurrency || 'VND',
            sex: profileToLoad.sex || '',
            employmentType: metadata.employmentType || (profileToLoad as any).employmentType || 'part-time',
            vacationDaysPerYear: metadata.vacationDaysPerYear ?? (profileToLoad as any).vacationDaysPerYear ?? 0,
            vacationDaysUsed: metadata.vacationDaysUsed ?? (profileToLoad as any).vacationDaysUsed ?? 0,
            baseSalary: metadata.baseSalary ?? (profileToLoad as any).baseSalary ?? 0,
            guaranteedHoursPerWeek: metadata.guaranteedHoursPerWeek ?? (profileToLoad as any).guaranteedHoursPerWeek ?? 0,
          });
        } else {
          // Initialize with default values if no profile exists
          console.log('📋 No profile found, initializing with default values');
          setFormData({
            address: '',
            postionId: '',
            departmentId: '',
            ratePerHour: 0,
            ratePerHourCurrency: 'VND',
            sex: '',
            employmentType: 'part-time',
            vacationDaysPerYear: 0,
            vacationDaysUsed: 0,
            baseSalary: 0,
            guaranteedHoursPerWeek: 0,
          });
        }
        setError(null);
      };
      
      loadProfileData();
    }
  }, [open, profile, userId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Determine the userId - use profile's userId if available, otherwise use passed userId
    const targetUserId = profile?.userId || userId;
    
    if (!targetUserId) {
      setError(
        translate(
          'pos.staff.profile.user_id_required',
          'User ID is required to create a profile'
        )
      );
      return;
    }

    setLoading(true);

    try {
      // Prepare data
      const profileData: any = {
        address: formData.address,
        postionId: formData.postionId,
        departmentId: formData.departmentId,
        ratePerHour: formData.ratePerHour,
        ratePerHourCurrency: formData.ratePerHourCurrency,
        sex: formData.sex,
      };

      // Store extended fields in metadata
      const metadata = {
        employmentType: formData.employmentType,
        vacationDaysPerYear: formData.vacationDaysPerYear,
        vacationDaysUsed: formData.vacationDaysUsed,
        baseSalary: formData.baseSalary,
        guaranteedHoursPerWeek: formData.guaranteedHoursPerWeek,
      };

      // ALWAYS check for existing profiles first - one user should only have ONE profile
      // This ensures we never create duplicates, even if profile.id is missing
      console.log(`🔍 Checking for existing profiles for userId: ${targetUserId}`);
      const existingProfiles = await staffProfileService.getAllStaffProfilesByUserId(targetUserId);
      
      if (existingProfiles.length > 1) {
        // Multiple profiles exist - this is a data integrity issue
        const errorMsg = `Data integrity error: User has ${existingProfiles.length} profiles. Only 1 profile per user is allowed. Please run the merge script to fix: npx tsx scripts/merge-duplicate-staff-profiles.ts <user-email>`;
        console.error(`❌ ${errorMsg}`, existingProfiles.map(p => ({ id: p.id, createdAt: (p as any).createdAt })));
        setError(
          translate(
            'pos.staff.profile.duplicate_profiles_error',
            `Error: User has ${existingProfiles.length} profiles. Please contact administrator to merge duplicates.`
          )
        );
        notify(
          translate(
            'pos.staff.profile.duplicate_profiles_error',
            `Error: User has ${existingProfiles.length} profiles. Please contact administrator.`
          ),
          { type: 'error' }
        );
        setLoading(false);
        return; // Don't proceed with create/update
      }
      
      if (existingProfiles.length === 1) {
        // Exactly one profile exists - ALWAYS update it (never create)
        const existingProfile = existingProfiles[0];
        console.log(`✅ Found existing profile ${existingProfile.id}, updating...`);
        
        const existingMetadata = (existingProfile as any).metadata || {};
        profileData.metadata = { ...existingMetadata, ...metadata };
        
        // Also set fields directly in case the API supports them
        profileData.employmentType = formData.employmentType;
        profileData.vacationDaysPerYear = formData.vacationDaysPerYear;
        profileData.vacationDaysUsed = formData.vacationDaysUsed;
        profileData.baseSalary = formData.baseSalary;
        profileData.guaranteedHoursPerWeek = formData.guaranteedHoursPerWeek;

        await staffProfileService.updateStaffProfile(existingProfile.id, profileData);
        
        notify(
          translate(
            'pos.staff.profile.profile_updated',
            'Staff profile updated successfully'
          ),
          { type: 'success' }
        );
      } else {
        // No profiles exist - create new one
        console.log(`📝 No existing profile found, creating new one for userId: ${targetUserId}`);
        profileData.userId = targetUserId;
        profileData.metadata = metadata;
        
        // Also set fields directly in case the API supports them
        profileData.employmentType = formData.employmentType;
        profileData.vacationDaysPerYear = formData.vacationDaysPerYear;
        profileData.vacationDaysUsed = formData.vacationDaysUsed;
        profileData.baseSalary = formData.baseSalary;
        profileData.guaranteedHoursPerWeek = formData.guaranteedHoursPerWeek;

        await staffProfileService.createStaffProfile(profileData);
        
        notify(
          translate(
            'pos.staff.profile.profile_created',
            'Staff profile created successfully'
          ),
          { type: 'success' }
        );
      }

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : profile?.id
          ? translate(
              'pos.staff.profile.profile_update_failed',
              'Failed to update staff profile'
            )
          : translate(
              'pos.staff.profile.profile_create_failed',
              'Failed to create staff profile'
            );
      setError(errorMessage);
      console.error(
        `Error ${profile?.id ? 'updating' : 'creating'} staff profile:`,
        err
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const isFullTime = formData.employmentType === 'full-time';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            {translate(
              'pos.staff.profile.edit_profile',
              'Edit Staff Profile'
            )}
          </DialogTitle>
          <DialogDescription>
            {translate(
              'pos.staff.profile.edit_profile_description',
              'Update staff profile information including employment type and compensation'
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {translate('pos.staff.profile.basic_information', 'Basic Information')}
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address">
                  {translate('pos.staff.profile.address', 'Address')}
                </Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={translate('pos.staff.profile.address_placeholder', 'Enter address')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sex">
                  {translate('pos.staff.profile.gender', 'Gender')}
                </Label>
                <Input
                  id="sex"
                  name="sex"
                  value={formData.sex}
                  onChange={handleChange}
                  placeholder={translate('pos.staff.profile.gender_placeholder', 'Enter gender')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postionId">
                  {translate('pos.staff.profile.position_id', 'Position ID')}
                </Label>
                <Input
                  id="postionId"
                  name="postionId"
                  value={formData.postionId}
                  onChange={handleChange}
                  placeholder={translate('pos.staff.profile.position_id_placeholder', 'Enter position ID')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="departmentId">
                  {translate('pos.staff.profile.department_id', 'Department ID')}
                </Label>
                <Input
                  id="departmentId"
                  name="departmentId"
                  value={formData.departmentId}
                  onChange={handleChange}
                  placeholder={translate('pos.staff.profile.department_id_placeholder', 'Enter department ID')}
                />
              </div>
            </div>
          </div>

          {/* Employment Type */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {translate('pos.staff.profile.employment_information', 'Employment Information')}
            </h3>

            <div className="space-y-2">
              <Label htmlFor="employmentType">
                {translate('pos.staff.profile.employment_type', 'Employment Type')}
              </Label>
              <Select
                name="employmentType"
                value={formData.employmentType}
                onValueChange={(value: 'full-time' | 'part-time' | 'contract') =>
                  setFormData((prev) => ({ ...prev, employmentType: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="part-time">
                    {translate('pos.staff.profile.part_time', 'Part-Time')}
                  </SelectItem>
                  <SelectItem value="full-time">
                    {translate('pos.staff.profile.full_time', 'Full-Time')}
                  </SelectItem>
                  <SelectItem value="contract">
                    {translate('pos.staff.profile.contract', 'Contract')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isFullTime && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                <h4 className="font-medium">
                  {translate('pos.staff.profile.full_time_settings', 'Full-Time Settings')}
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vacationDaysPerYear">
                      {translate('pos.staff.profile.vacation_days_per_year', 'Vacation Days Per Year')}
                    </Label>
                    <Input
                      id="vacationDaysPerYear"
                      name="vacationDaysPerYear"
                      type="number"
                      min="0"
                      value={formData.vacationDaysPerYear}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vacationDaysUsed">
                      {translate('pos.staff.profile.vacation_days_used', 'Vacation Days Used')}
                    </Label>
                    <Input
                      id="vacationDaysUsed"
                      name="vacationDaysUsed"
                      type="number"
                      min="0"
                      value={formData.vacationDaysUsed}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="baseSalary">
                      {translate('pos.staff.profile.base_salary', 'Base Salary (Optional)')}
                    </Label>
                    <Input
                      id="baseSalary"
                      name="baseSalary"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.baseSalary}
                      onChange={handleChange}
                      placeholder="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      {translate('pos.staff.profile.base_salary_hint', 'Monthly base salary if applicable')}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="guaranteedHoursPerWeek">
                      {translate('pos.staff.profile.guaranteed_hours', 'Guaranteed Hours Per Week')}
                    </Label>
                    <Input
                      id="guaranteedHoursPerWeek"
                      name="guaranteedHoursPerWeek"
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.guaranteedHoursPerWeek}
                      onChange={handleChange}
                      placeholder="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      {translate('pos.staff.profile.guaranteed_hours_hint', 'Minimum hours guaranteed per week')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Compensation */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {translate('pos.staff.profile.compensation', 'Compensation')}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ratePerHour">
                  {translate('pos.staff.profile.rate_per_hour', 'Rate Per Hour')}
                </Label>
                <Input
                  id="ratePerHour"
                  name="ratePerHour"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.ratePerHour}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ratePerHourCurrency">
                  {translate('pos.staff.profile.currency', 'Currency')}
                </Label>
                <Select
                  name="ratePerHourCurrency"
                  value={formData.ratePerHourCurrency}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, ratePerHourCurrency: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VND">VND</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                  {translate('pos.common.action.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {translate('pos.common.action.save', 'Save')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

